import React, { useState, useEffect } from 'react';
// Corrected Paths for common components
import AppPaginator from '../../AppPaginator';
import AppSearchBox from '../../AppSearchBox';
import AppBreadCrumb from '../../common/AppBreadCrumb';
import PerPageBlock from '../../common/PerPageBlock';
import AppToaster from '../../AppToaster';
// API and Utils (Corrected Paths)
import { destroyHoa, destroyHoasMultiple, fetchHoa, getHoas } from '../../../api/HoasAPI';
import { handleApiError } from '../../../api/risapi';
// import HoasReport from './HoasReport'; // Assuming path is correct
import { useLocation, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
// import { getAvatar } from '../../../commonfunctions'; // Removed if not used for HOA

const HoasPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const successMessage = location.state?.message;

    const [entityName, setEntityName] = useState('Head of Account'); // More descriptive name
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [reportLoading, setReportLoading] = useState(false);
    const [viewDataLoading, setViewDataLoading] = useState(false);
    const [searchTermCompleted, setSearchTermCompleted] = useState('');
    const [perPage, setPerPage] = useState(process.env.REACT_APP_PER_PAGE_COUNT || 10);
    const [enableMultipleItemSelect, setEnableMultipleItemSelect] = useState(false);

    const [hoasList, setHoasList] = useState({
        paginate: true,
        data: [],
        per_page: perPage,
        current_page: 1,
        tot_page: null,
    });

    const [toaster, setToaster] = useState({});
    const [showToaster, setShowToaster] = useState(false);
    const [saveCompleted, setSaveCompleted] = useState(false);
    const [selectedHoaIds, setSelectedHoaIds] = useState(new Set());

    const handleAddClick = () => navigate('/franchisee_user/masters/hoas/create');
    const handleEditClick = (hoaId) => navigate(`/franchisee_user/masters/hoas/edit/${hoaId}`);

    const handleDeleteClick = (hoaId) => {
        fetchHoa(hoaId, function (result) {
            if (result.hoa) {
                const deleteHoa = result.hoa;

                // ** Prevent deleting system HOAs **
                if (deleteHoa.is_system_defined) {
                    Swal.fire('Action Denied', 'System-defined Heads of Account cannot be deleted.', 'warning');
                    return;
                }

                Swal.fire({
                    title: 'Are you sure?',
                    html: `... delete HOA: ${deleteHoa.name} ...`, // Simplified HTML
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonColor: '#d33',
                    cancelButtonColor: '#6c757d',
                    confirmButtonText: 'Yes, delete it!'
                }).then((dialogResult) => {
                    if (dialogResult.isConfirmed) {
                        destroyHoa(deleteHoa, function (apiResponse) {
                            if (apiResponse.hoa) {
                                Swal.fire('Deleted!', 'Record deleted.', 'success');
                                setSaveCompleted(prev => !prev);
                            } else {
                                // Display specific error from backend if available
                                Swal.fire('Error!', apiResponse.error?.response?.data?.message || 'Could not delete.', 'error');
                            }
                        });
                    }
                });
            } else {
                 Swal.fire('Error!', 'Could not fetch details.', 'error');
            }
        });
    };

     const showMultipleDeleteConfirmation = () => {
         // Filter out system-defined HOAs BEFORE showing confirmation
         const deletableHoas = hoasList.data.filter(hoa =>
             selectedHoaIds.has(hoa.id) && !hoa.is_system_defined
         );
         const systemHoasSelected = hoasList.data.filter(hoa =>
             selectedHoaIds.has(hoa.id) && hoa.is_system_defined
         );

         if (deletableHoas.length === 0) {
              let message = 'No deletable Heads of Account selected.';
              if (systemHoasSelected.length > 0) {
                  message += ' System-defined accounts cannot be deleted.';
              }
              Swal.fire('No Action', message, 'info');
              return;
         }

        Swal.fire({
            title: `Delete ${deletableHoas.length} Heads of Account?`,
            html: `
                <p>You are about to delete the following items:</p>
                <div style="max-height: 150px; overflow-y: auto;">
                    ${deletableHoas.map(hoa => `
                        <ul class="delete-details-list mb-2">
                            <li class="details-row"><span><strong>ID:</strong></span><span>${hoa.id}</span></li>
                            <li class="details-row"><span><strong>Name:</strong></span><span>${hoa.name}</span></li>
                        </ul>
                    `).join('')}
                </div>
                 ${systemHoasSelected.length > 0 ? `<p class="mt-2 text-warning">Note: ${systemHoasSelected.length} system-defined account(s) were selected but will NOT be deleted.</p>` : ''}
                <p class="mt-2 text-danger">This action cannot be undone.</p>`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Yes, delete them!'
        }).then((result) => {
            if (result.isConfirmed) {
                const idsToDelete = deletableHoas.map(hoa => hoa.id); // Only include deletable IDs

                destroyHoasMultiple(idsToDelete, function (response) {
                    if (response.hoa) {
                        Swal.fire('Deleted!', 'Selected records deleted.', 'success');
                        setEnableMultipleItemSelect(false);
                        setSelectedHoaIds(new Set());
                        setSaveCompleted(prev => !prev);
                    } else {
                        Swal.fire('Error!', response.error?.response?.data?.message || 'Could not delete.', 'error');
                    }
                });
            }
        });
    };


    const handleCheckboxChange = (id, isChecked) => {
        setSelectedHoaIds(prevSelectedIds => {
            const newSelectedIds = new Set(prevSelectedIds);
            if (isChecked) newSelectedIds.add(id);
            else newSelectedIds.delete(id);
            return newSelectedIds;
        });
    };

    useEffect(() => {
        if (successMessage) {
            setShowToaster(true);
            setToaster({ type: 'success', heading: 'Success', message: successMessage });
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [successMessage, location.pathname, navigate]);

    useEffect(() => {
        document.title = 'Manage Heads of Account';
        let filters = { paginate: true, page: currentPage, per_page: perPage, search_term: searchTermCompleted };

        setViewDataLoading(true);
        setSelectedHoaIds(new Set());
        setEnableMultipleItemSelect(false);

        getHoas(filters, function (response) {
            setViewDataLoading(false);
            if (response.hoas?.data) { // Check specifically for paginated data
                setHoasList({
                    paginate: true,
                    per_page: response.hoas.per_page,
                    data: response.hoas.data,
                    current_page: response.hoas.current_page,
                    tot_page: response.hoas.last_page,
                });
            } else if (Array.isArray(response.hoas)) { // Handle non-paginated or empty array
                 setHoasList({ paginate: false, data: response.hoas, per_page: response.hoas.length, current_page: 1, tot_page: 1});
            }
             else {
                handleApiError(response);
                setToaster({ type: 'danger', heading: 'Error', message: 'Could not load Heads of Account.' });
                setShowToaster(true);
                setHoasList({ paginate: true, data: [], per_page: perPage, current_page: 1, tot_page: 1 }); // Safe reset
            }
        });
    }, [currentPage, searchTermCompleted, saveCompleted, perPage]);

    return (
        <>
            <AppToaster showToaster={showToaster} setShowToaster={setShowToaster} toaster={toaster} />
            <main>
                <div className="container-fluid">
                    <AppBreadCrumb title='Heads of Account List'>
                        <li><a className="f-s-14 f-w-500" href="#">Masters</a></li>
                        <li className="active"><a className="f-s-14 f-w-500" href="#">Heads of Account</a></li>
                    </AppBreadCrumb>

                    <div className="row bg-white">
                        <div className="col-12">
                            <div className="tab-wrapper mb-3">
                                <ul className="tabs">
                                    <li className="tab-link active" data-tab={1}>All Heads of Account</li>
                                    <li className="ms-md-auto">
                                        <div className="text-md-end d-flex flex-column flex-md-row mt-md-3">
                                            <AppSearchBox searchTerm={searchTerm} setSearchTerm={setSearchTerm} setSearchTermCompleted={setSearchTermCompleted} />
                                            <button className="text-nowrap btn btn-red btn-sm ms-md-3 fw-semibold f-s-14" type="button" onClick={handleAddClick}>
                                                <i className="ph-bold  ph-circles-three-plus"></i> Add HOA
                                            </button>
                                            {/* <HoasReport reportLoading={reportLoading} setReportLoading={setReportLoading} /> */}
                                        </div>
                                    </li>
                                </ul>
                            </div>
                            <div className="content-wrapper" id="card-container">
                                <div className="d-flex flex-wrap m-md-3">
                                    {hoasList.data.length > 0 ? (
                                        <>
                                            <AppPaginator entityName={entityName} currentPage={hoasList.current_page} totalPages={hoasList.tot_page} setCurrentPage={setCurrentPage} />
                                            <PerPageBlock perPage={perPage} setPerPage={setPerPage} />
                                            <div className='d-flex align-items-center ms-auto mt-2 mt-md-0'>
                                                <button className="btn btn-info btn-sm me-2" type="button" onClick={() => setEnableMultipleItemSelect(prev => !prev)}>
                                                    {enableMultipleItemSelect ? (<><i className="fa-solid fa-check fa-fw me-1" />Done</>) : 'Select'}
                                                </button>
                                                {enableMultipleItemSelect && selectedHoaIds.size > 0 && (
                                                    <button className="btn btn-danger btn-sm" type="button" onClick={showMultipleDeleteConfirmation}> Delete ({selectedHoaIds.size}) Records </button>
                                                )}
                                            </div>
                                        </>
                                    ) : ( !viewDataLoading && <p className="text-center w-100">No Heads of Account found.</p> )}
                                    {(reportLoading || viewDataLoading) && <div className="loader_box ms-auto"><div className="loader_14"></div></div>}
                                </div>

                                <div className="tabs-content active" id="tab-1">
                                    <div className="row">
                                        {!viewDataLoading && hoasList.data.map((hoa) => (
                                            <div className="col-md-6 col-xl-4 project-card" key={hoa.id}>
                                                <div className="card hover-effect">
                                                    <div className="card-header">
                                                        <div className="d-flex align-items-center">
                                                            {enableMultipleItemSelect && (
                                                                <input className="form-check-input me-2" type="checkbox" checked={selectedHoaIds.has(hoa.id)} onChange={(e) => handleCheckboxChange(hoa.id, e.target.checked)} id={`select-hoa-${hoa.id}`} />
                                                            )}
                                                            <div className="h-40 w-40 d-flex-center b-r-50 overflow-hidden me-2 btn-light-reddit">
                                                                 <i className="ti ti-file-invoice f-s-18"></i> {/* Icon for HOA */}
                                                            </div>
                                                            <div className="flex-grow-1 ps-2">
                                                                <h6 className="m-0 text-dark f-w-600">{hoa.name}</h6>
                                                                {/* Show badges */}
                                                                 <div className='mt-1'>
                                                                     {hoa.is_system_defined && <span className="badge bg-danger me-1">System</span>}
                                                                     {hoa.is_gst_head && <span className="badge bg-info">GST</span>}
                                                                 </div>
                                                            </div>
                                                            <div className="dropdown">
                                                                <button aria-expanded="false" className="bg-none border-0" data-bs-toggle="dropdown" type="button"><i className="ti ti-dots-vertical text-dark" /></button>
                                                                <ul className="dropdown-menu dropdown-menu-end">
                                                                    <li><a className="dropdown-item" onClick={() => handleEditClick(hoa.id)}><i className="ti ti-edit text-success me-1" /> Edit</a></li>
                                                                    <li>
                                                                        {/* Disable delete for system HOAs */}
                                                                        <button
                                                                            className="dropdown-item delete-button"
                                                                            onClick={() => handleDeleteClick(hoa.id)}
                                                                            disabled={hoa.is_system_defined}
                                                                            style={hoa.is_system_defined ? { cursor: 'not-allowed', color: '#adb5bd' } : {}}
                                                                            title={hoa.is_system_defined ? "Cannot delete system-defined HOA" : "Delete HOA"}
                                                                        >
                                                                            <i className={`ti ti-trash me-1 ${hoa.is_system_defined ? 'text-muted' : 'text-danger'}`} /> Delete
                                                                        </button>
                                                                    </li>
                                                                </ul>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    {/* Optional: Add more details to card body if needed */}
                                                    {/* <div className="card-body"> ... </div> */}
                                                </div>
                                            </div>
                                        ))}
                                        {!viewDataLoading && hoasList.data.length === 0 && (
                                            <div className="col-12"> <div className="card mb-3"><div className="row g-0"><div className="col-md-12"><div className="card-body"><h5 className="card-title">No Heads of Account Found</h5><p className="card-text">No records match your criteria or none have been added yet.</p></div></div></div></div></div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        </>
    );
};

export default HoasPage;